<script setup lang="ts">
import { Carousel, Navigation, Slide } from 'vue3-carousel';
import type { Area } from '@ilteducation/content-ref-data-types';
import 'vue3-carousel/dist/carousel.css';
import { computed, ref } from 'vue';
import useKeywordsStore, { KeywordWithTranslations } from '@/store/keywords';
import useLanguagesStore from '@/store/languages';
import useIsRtlLanguage from '@/composables/use-is-rtl-language';
import { useInertHidden } from '@/composables/useInertHidden';
import { useLocale } from '@/plugins/translation';

const props = defineProps<{
  carouselItems: Area['carouselKeywords'];
  altTexts: Record<string, string>;
  showTranslations?: boolean;
  selectedLanguage?: string;
}>();

const emit = defineEmits<{
  (e: 'carouselClicked', carouselKeywordId: string, carouselPosition: number): void;
  (e: 'carouselScrolled', scrollDirection: 'next' | 'prev', fromPosition: number, toPosition: number): void;
}>();

const keywordsStore = useKeywordsStore();
const { getKeywordImageUrl } = keywordsStore;
const languageStore = useLanguagesStore();
const { t, l } = useLocale();

const carouselKeywords = computed(() =>
  props.carouselItems?.map((ci) => keywordsStore.getByKey(ci.key)).filter((k): k is KeywordWithTranslations => !!k),
);
const carousel = ref();
const next = () => carousel.value.next();
const prev = () => carousel.value.prev();

useInertHidden(carousel);

const flippedIndexes = ref(new Set<number>());

const handleCarouselClick = (event: Event, index: number) => {
  const container = (event.target as HTMLElement).closest('.flip-container');
  if (!container) {
    return;
  }

  if (container.classList.contains('carousel__slide--active')) {
    container.classList.toggle('active');
    if (flippedIndexes.value.has(index)) {
      flippedIndexes.value.delete(index);
    } else {
      flippedIndexes.value.add(index);
      emit('carouselClicked', carouselKeywords.value![index].id, index);
    }
  } else if (container.classList.contains('carousel__slide--next')) {
    next();
  } else if (container.classList.contains('carousel__slide--prev')) {
    prev();
  }
};

const getAriaLabel = (index: number) => {
  if (!props.carouselItems) {
    return '';
  }
  const total = props.carouselItems.length;
  const current = carousel.value?.data.currentSlide.value ?? 0;
  const nextIndex = (current + 1) % total;
  const prevIndex = (current - 1 + total) % total;

  if (index === current) {
    return t(l.area_guide.carousel.button_flip_image);
  }
  if (index === prevIndex) {
    return t(l.area_guide.carousel.label_prev);
  }
  if (index === nextIndex) {
    return t(l.area_guide.carousel.label_next);
  }
  return ' ';
};

const normalizeSlideIndex = (index: number, slidesCount: number) => ((index % slidesCount) + slidesCount) % slidesCount;

const handleSlideStart = (data: { currentSlideIndex: number; slidingToIndex: number; slidesCount: number }) => {
  const isNext = data.slidingToIndex > data.currentSlideIndex;
  const from = normalizeSlideIndex(data.currentSlideIndex, data.slidesCount);
  const to = normalizeSlideIndex(data.slidingToIndex, data.slidesCount);
  emit('carouselScrolled', isNext ? 'next' : 'prev', from, to);
};

const breakpoints = {
  // 600px and up
  600: {
    itemsToShow: 1.5,
  },
};
const isRTL = computed(() => props.selectedLanguage && useIsRtlLanguage(props.selectedLanguage));
</script>
<template>
  <Carousel
    ref="carousel"
    wrap-around
    :items-to-show="1"
    :transition="600"
    :breakpoints="breakpoints"
    @slide-start="handleSlideStart"
  >
    <Slide v-for="(carouselItem, i) in carouselKeywords" :key="i" :index="i" class="flip-container">
      <button
        class="carousel__item flipper"
        @click="(event) => handleCarouselClick(event, i)"
        :aria-label="getAriaLabel(i)"
      >
        <div class="front">
          <v-img
            v-if="carouselItem.imageId"
            cover
            :src="getKeywordImageUrl(carouselItem)"
            :aspect-ratio="4 / 3"
            :alt="altTexts[carouselItem.imageId]"
          />
        </div>
        <div class="back bg-white pa-6 pa-md-9" :aria-hidden="!flippedIndexes.has(i)">
          <h3>
            {{ carouselItem.translations?.title?.[languageStore.getMarketLanguage()].translation }}
          </h3>
          <p>
            {{ carouselItem.translations?.description?.[languageStore.getMarketLanguage()].translation }}
          </p>
          <template v-if="showTranslations && selectedLanguage">
            <v-divider class="w-100 my-4" :aria-label="t(l.label_language_divider)" />
            <h4>
              {{ carouselItem.translations?.title?.[selectedLanguage]?.translation }}
            </h4>
            <p :dir="isRTL ? 'rtl' : 'ltr'">
              {{ carouselItem.translations?.description?.[selectedLanguage]?.translation }}
            </p>
          </template>
        </div>
      </button>
    </Slide>
    <template #addons>
      <Navigation />
    </template>
  </Carousel>
</template>
<style scoped lang="scss">
@use '@/foundations/theme/settings';
@use 'sass:map';

.carousel {
  max-width: 900px;
  min-height: unset !important;
}

.carousel__slide {
  min-width: 200px;
  padding: 12px;
  opacity: 0.9;
  transform: rotateY(-20deg) scale(0.9);
}

.carousel__track {
  transform-style: preserve-3d;
}

.carousel__slide--sliding {
  transition: 0.5s;
}

.carousel__slide--next,
.carousel__slide--prev {
  @media #{map.get(settings.$display-breakpoints, 'xs')} {
    transform: none !important;
  }
  &:after {
    position: absolute;
    inset: 0;
    content: '';
    z-index: 999;
    opacity: 0;
    animation: fadeIn 0.3s ease 0.4s forwards;
  }
}

.carousel__slide--next {
  position: relative;
  opacity: 0.8;
  transform: rotateY(20deg) translateX(-60%) scale(0.65);
  z-index: -1;

  &:after {
    background: linear-gradient(to right, white 60%, transparent 75%);
    pointer-events: none;
  }
}

.carousel__slide--prev {
  position: relative;
  opacity: 0.8;
  transform: rotateY(-20deg) scale(0.65) translateX(90%);

  &:after {
    background: linear-gradient(to left, white 60%, transparent 75%);
    pointer-events: none;
  }
}

.carousel__slide--active {
  opacity: 1;
  transform: rotateY(0) scale(1);
}

.flipper {
  min-width: 200px;
  width: 100%;
  height: 100%;
  transition: 0.6s;
  transform-style: preserve-3d;
  position: relative;
  z-index: 2;

  .front,
  .back {
    backface-visibility: hidden;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 1px 10px 1px rgba(0, 0, 0, 0.25);
    width: 100%;
  }

  .front {
    z-index: 2;
    /* for firefox 31 */
    transform: rotateY(0deg);
  }

  .back {
    transform: rotateY(180deg);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
  }
}

.active .flipper {
  transform: rotateY(180deg);
}

@keyframes fadeIn {
  to {
    opacity: 0.75;
  }
}
</style>
